import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, Mail, Users, ExternalLink, Settings as SettingsIcon } from "lucide-react";

const TABS = [
  { id: "modules", label: "Modules" },
  { id: "tiers", label: "Tiers" },
  { id: "prompts", label: "Prompts" },
  { id: "blog", label: "Blog" },
  { id: "testimonials", label: "Testimonials" },
  { id: "services", label: "EB · Services" },
  { id: "case_studies", label: "EB · Case Studies" },
  { id: "faqs", label: "FAQs" },
  { id: "uploads", label: "Files" },
  { id: "leads", label: "Leads" },
  { id: "newsletter", label: "Newsletter" },
  { id: "settings", label: "Settings" },
];

// Field schemas per collection
const SCHEMA = {
  modules: [
    { k: "slug", label: "Slug (unique id)", required: true },
    { k: "number", label: "Order number (01-07)" },
    { k: "title", label: "Title", required: true },
    { k: "tagline", label: "Tagline" },
    { k: "level", label: "Level (Foundation / Core / Advanced)" },
    { k: "duration_hours", label: "Duration (hours)", type: "number" },
    { k: "lessons", label: "Lessons", type: "number" },
    { k: "description", label: "Description", multiline: true },
    { k: "image", label: "Image URL" },
    { k: "outcomes", label: "Outcomes (one per line)", multiline: true, list: true },
    { k: "tags", label: "Tags (comma separated)", listCsv: true },
  ],
  tiers: [
    { k: "slug", label: "Slug (starter/creator/mastery)", required: true },
    { k: "name", label: "Name", required: true },
    { k: "price", label: "Price (USD)", type: "number" },
    { k: "popular", label: "Mark as popular", type: "bool" },
    { k: "gumroad_product_id", label: "Gumroad product_id (from Gumroad → product → License Keys)" },
    { k: "cta_url", label: "Gumroad checkout URL" },
    { k: "cta_label", label: "CTA label" },
    { k: "tagline", label: "Tagline", multiline: true },
    { k: "learn", label: "Learn list (one per line)", multiline: true, list: true },
    { k: "included", label: "Included list (one per line)", multiline: true, list: true },
    { k: "best_for", label: "Best for (comma separated)", listCsv: true },
  ],
  prompts: [
    { k: "title", label: "Title", required: true },
    { k: "category", label: "Category" },
    { k: "preview", label: "Prompt text", multiline: true },
    { k: "tags", label: "Tags (comma separated)", listCsv: true },
  ],
  blog: [
    { k: "slug", label: "Slug (unique id)", required: true },
    { k: "title", label: "Title", required: true },
    { k: "tag", label: "Tag" },
    { k: "read_minutes", label: "Read minutes", type: "number" },
    { k: "excerpt", label: "Excerpt", multiline: true },
    { k: "body", label: "Body", multiline: true, big: true },
    { k: "image", label: "Image URL" },
  ],
  testimonials: [
    { k: "name", label: "Customer name", required: true },
    { k: "title", label: "Title / handle" },
    { k: "quote", label: "Quote", multiline: true, required: true },
    { k: "avatar_url", label: "Avatar URL (optional)" },
    { k: "order", label: "Order (lower = first)", type: "number" },
    { k: "published", label: "Published", type: "bool" },
  ],
  services: [
    { k: "slug", label: "Slug (unique id)", required: true },
    { k: "title", label: "Title", required: true },
    { k: "summary", label: "Short summary" },
    { k: "body", label: "Body copy", multiline: true },
    { k: "order", label: "Order (lower = first)", type: "number" },
  ],
  case_studies: [
    { k: "slug", label: "Slug (unique id)", required: true },
    { k: "client", label: "Client name", required: true },
    { k: "project", label: "Project name" },
    { k: "tag", label: "Tag (e.g. Hospitality)" },
    { k: "year", label: "Year" },
    { k: "hero_image", label: "Hero image URL" },
    { k: "video_url", label: "Video URL (Vimeo / YouTube embed)" },
    { k: "brief", label: "The brief", multiline: true },
    { k: "outcome", label: "Outcome", multiline: true },
    { k: "deliverables", label: "Deliverables (one per line)", multiline: true, list: true },
    { k: "gallery", label: "Gallery image URLs (one per line)", multiline: true, list: true },
    { k: "order", label: "Order (lower = first)", type: "number" },
  ],
  faqs: [
    { k: "category", label: "Category (academy | studio)", required: true },
    { k: "question", label: "Question", required: true },
    { k: "answer", label: "Answer", multiline: true, required: true },
    { k: "order", label: "Order (lower = first)", type: "number" },
    { k: "published", label: "Published", type: "bool" },
  ],
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("modules");

  if (loading) return <div className="p-20 text-center text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!user.is_admin) return <div className="p-20 text-center text-gray-500">Admin access only.</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="label-eyebrow mb-3">CMS</div>
      <h1 className="text-4xl font-black tracking-tight font-display-ai">Admin</h1>
      <p className="text-gray-400 mt-3 text-sm">Edit every piece of site content. Changes are live immediately.</p>

      <div className="mt-8 flex flex-wrap gap-2 border-b hairline pb-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`px-4 py-2 rounded-full text-xs tracking-wide border ${
              tab === t.id ? "bg-[#00FFC2] text-black border-transparent" : "hairline hover:bg-white/5"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {SCHEMA[tab] && <CrudTab kind={tab} schema={SCHEMA[tab]} />}
        {tab === "uploads" && <UploadsTab />}
        {tab === "leads" && <ReadOnlyList kind="leads" cols={["created_at", "name", "email", "company", "kind", "message"]} />}
        {tab === "newsletter" && <ReadOnlyList kind="newsletter" cols={["created_at", "email"]} />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

function CrudTab({ kind, schema }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await http.get(`/admin/${kind}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); }, [kind]);

  const startNew = () => {
    const blank = {};
    schema.forEach((f) => { blank[f.k] = f.type === "bool" ? false : f.list || f.listCsv ? [] : ""; });
    setEditing(blank);
  };

  const save = async (item) => {
    // serialize list fields back
    const payload = { ...item };
    schema.forEach((f) => {
      if (f.list && typeof item[f.k] === "string") payload[f.k] = item[f.k].split("\n").map((s) => s.trim()).filter(Boolean);
      if (f.listCsv && typeof item[f.k] === "string") payload[f.k] = item[f.k].split(",").map((s) => s.trim()).filter(Boolean);
      if (f.type === "number" && payload[f.k] !== "" && payload[f.k] != null) payload[f.k] = Number(payload[f.k]);
      if (f.type === "bool") payload[f.k] = !!payload[f.k];
    });
    try {
      await http.put(`/admin/${kind}`, payload);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (ident) => {
    if (!window.confirm("Delete this permanently?")) return;
    try {
      await http.delete(`/admin/${kind}/${encodeURIComponent(ident)}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const idKey = schema.find((f) => f.required)?.k || schema[0].k;
  const titleKey = ["title", "name", "question", "client", "slug"].find((k) => schema.find((f) => f.k === k)) || idKey;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-400">{items.length} item{items.length === 1 ? "" : "s"}</div>
        <button onClick={startNew} data-testid={`admin-${kind}-new`} className="btn-mint rounded-full px-4 py-2 text-xs inline-flex items-center gap-2">
          <Plus size={14} /> New
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {items.map((it) => (
          <div key={it[idKey]} className="glass rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`admin-${kind}-row-${it[idKey]}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{it[titleKey] || it[idKey]}</div>
              <div className="text-xs text-gray-400 truncate">{it[idKey]}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing({ ...it })} className="text-xs text-gray-300 hover:text-white">Edit</button>
              <button onClick={() => remove(it[idKey])} className="text-xs text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditModal item={editing} schema={schema} onCancel={() => setEditing(null)} onSave={save} kind={kind} />
      )}
    </div>
  );
}

function EditModal({ item, schema, onCancel, onSave, kind }) {
  const [draft, setDraft] = useState(() => {
    const d = { ...item };
    schema.forEach((f) => {
      if (f.list && Array.isArray(d[f.k])) d[f.k] = d[f.k].join("\n");
      if (f.listCsv && Array.isArray(d[f.k])) d[f.k] = d[f.k].join(", ");
    });
    return d;
  });
  const set = (k, v) => setDraft({ ...draft, [k]: v });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur" data-testid="admin-edit-modal">
      <div className="glass rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold font-display-ai">Edit {kind.slice(0, -1) === "tier" ? "tier" : kind.slice(0, -1)}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-white text-sm">Cancel</button>
        </div>
        <div className="space-y-3">
          {schema.map((f) => (
            <div key={f.k}>
              <label className="text-xs text-gray-400 mb-1.5 block">{f.label}{f.required && <span className="text-[#A855F7]"> *</span>}</label>
              {f.type === "bool" ? (
                <button
                  type="button"
                  onClick={() => set(f.k, !draft[f.k])}
                  className={`px-3 py-1.5 rounded-full text-xs border ${draft[f.k] ? "bg-[#00FFC2] text-black border-transparent" : "hairline"}`}
                  data-testid={`admin-field-${f.k}`}
                >
                  {draft[f.k] ? "Yes" : "No"}
                </button>
              ) : f.multiline ? (
                <textarea
                  rows={f.big ? 12 : 4}
                  value={draft[f.k] ?? ""}
                  onChange={(e) => set(f.k, e.target.value)}
                  data-testid={`admin-field-${f.k}`}
                  className="w-full bg-black/40 border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00FFC2]/60"
                />
              ) : (
                <input
                  type={f.type === "number" ? "number" : "text"}
                  value={draft[f.k] ?? ""}
                  onChange={(e) => set(f.k, e.target.value)}
                  data-testid={`admin-field-${f.k}`}
                  className="w-full bg-black/40 border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00FFC2]/60"
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-full text-xs border hairline hover:bg-white/5">Cancel</button>
          <button onClick={() => onSave(draft)} data-testid="admin-edit-save" className="btn-mint rounded-full px-5 py-2 text-xs inline-flex items-center gap-2">
            <Save size={12} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadsTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const load = async () => {
    try {
      const { data } = await http.get("/admin/uploads");
      setItems(data.items);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("label", label);
      await http.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setLabel("");
      load();
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete file permanently?")) return;
    await http.delete(`/admin/uploads/${id}`);
    load();
  };

  return (
    <div>
      <div className="glass rounded-xl p-5 mb-5 flex flex-wrap items-center gap-3">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (optional, e.g. 'Faceless Code PDF')"
          className="flex-1 min-w-[200px] bg-black/40 border hairline rounded-md px-3 py-2 text-sm"
        />
        <label className="btn-mint rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2 cursor-pointer" data-testid="admin-upload-input-label">
          <Upload size={14} /> {busy ? "Uploading…" : "Upload file"}
          <input type="file" hidden onChange={onPick} disabled={busy} data-testid="admin-upload-input" />
        </label>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {items.map((u) => (
          <div key={u.id} className="glass rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`admin-upload-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{u.label || u.name}</div>
              <div className="text-xs text-gray-400 truncate">{u.name} · {Math.round((u.size || 0) / 1024)} KB</div>
            </div>
            <a href={u.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-300 inline-flex items-center gap-1">
              Open <ExternalLink size={11} />
            </a>
            <button onClick={() => remove(u.id)} className="text-xs text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-gray-500">No files uploaded yet.</div>}
      </div>
    </div>
  );
}

function ReadOnlyList({ kind, cols }) {
  const [items, setItems] = useState([]);
  useEffect(() => { http.get(`/admin/${kind}`).then(({ data }) => setItems(data.items || [])); }, [kind]);

  const icon = kind === "leads" ? <Users size={14} /> : <Mail size={14} />;
  return (
    <div>
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-400">
        {icon} {items.length} total
      </div>
      <div className="overflow-x-auto glass rounded-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.25em] uppercase text-gray-400 border-b hairline">
              {cols.map((c) => <th key={c} className="py-3 px-4">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b hairline last:border-b-0" data-testid={`admin-${kind}-row-${i}`}>
                {cols.map((c) => (
                  <td key={c} className="py-3 px-4 text-gray-300 align-top max-w-[320px]">
                    <div className="truncate whitespace-pre-wrap break-words">{String(it[c] ?? "")}</div>
                  </td>
                ))}
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={cols.length} className="py-8 px-4 text-center text-gray-500">No entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState({ custom_gpt_url: "", gumroad_creator_url: "", calendly_url: "", editbae_email: "" });
  useEffect(() => {
    http.get("/admin/settings").then(({ data }) => setS({
      custom_gpt_url: data.settings?.custom_gpt_url || "",
      gumroad_creator_url: data.settings?.gumroad_creator_url || "",
      calendly_url: data.settings?.calendly_url || "",
      editbae_email: data.settings?.editbae_email || "",
    }));
  }, []);
  const save = async () => {
    try {
      await http.put("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); }
  };
  return (
    <div className="glass rounded-2xl p-7 max-w-2xl">
      <div className="flex items-center gap-2 mb-5 text-sm text-gray-400">
        <SettingsIcon size={14} /> Global settings
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">
            Custom GPT URL (shown to Creator & Mastery customers)
          </label>
          <input
            value={s.custom_gpt_url}
            onChange={(e) => setS({ ...s, custom_gpt_url: e.target.value })}
            placeholder="https://chatgpt.com/g/g-xxxxx-ai-creator-os-command-center"
            data-testid="settings-custom-gpt-url"
            className="w-full bg-black/40 border hairline rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">Primary Gumroad URL (used in CTAs)</label>
          <input
            value={s.gumroad_creator_url}
            onChange={(e) => setS({ ...s, gumroad_creator_url: e.target.value })}
            data-testid="settings-gumroad-url"
            className="w-full bg-black/40 border hairline rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">
            Calendly embed URL (Edit Bae Studios booking widget)
          </label>
          <input
            value={s.calendly_url}
            onChange={(e) => setS({ ...s, calendly_url: e.target.value })}
            placeholder="https://calendly.com/your-handle/discovery-call"
            data-testid="settings-calendly-url"
            className="w-full bg-black/40 border hairline rounded-md px-3 py-2 text-sm"
          />
          <div className="text-[11px] text-gray-500 mt-1">
            Paste your Calendly event URL. Leave empty to show the inquiry form only.
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">Edit Bae contact email</label>
          <input
            value={s.editbae_email}
            onChange={(e) => setS({ ...s, editbae_email: e.target.value })}
            placeholder="hello@editbaestudios.com"
            data-testid="settings-editbae-email"
            className="w-full bg-black/40 border hairline rounded-md px-3 py-2 text-sm"
          />
        </div>
        <button onClick={save} data-testid="settings-save" className="btn-mint rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2">
          <Save size={12} /> Save settings
        </button>
      </div>
      <div className="mt-8 pt-6 border-t hairline text-xs text-gray-500 leading-relaxed">
        <div className="font-semibold text-gray-300 mb-2">Tip — Gumroad license keys</div>
        On each Gumroad product (Starter / Creator / Mastery), enable license keys in the
        <strong> Content</strong> tab (Insert → License key), then paste each product's{" "}
        <strong>product_id</strong> into the matching row in <strong>Tiers</strong>. Customers
        paste their key at <code>/redeem</code> to unlock.
      </div>
    </div>
  );
}
